import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import LanguageIcon from "@mui/icons-material/Language";
import HandshakeIcon from "@mui/icons-material/Handshake";

const AboutCompany = ({ showTitle = true }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const themeColors = {
    primary: "#f8fbff",
    secondary: "#f8fbff",
    textDark: "#222",
    textLight: "#666",
    white: "#fff",
  };

  const cardsData = [
    {
      icon: <LanguageIcon sx={{ fontSize: 40, color: "#fff" }} />,
      title: "Global Expansion",
      desc: "Our international team communicates with you in your native language and guides you through expansion, audit, taxation, mergers, and incorporation, both within and outside India.",
      color: "#1a42a1",
    },
    {
      icon: <HandshakeIcon sx={{ fontSize: 40, color: "#fff" }} />,
      title: "Bespoke Services",
      desc: "We offer tailor-made services to assist your company formation in India, Europe, or Japan, helping you navigate any tax or legal-related complexities with precision.",
      color: "#2b5ce2",
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: "#fff" }} />,
      title: "Sustainable Growth",
      desc: "Expert guidance for entering Indian or international markets, ensuring full compliance, strategic planning, and sustainable long-term expansion.",
      color: "#3d7eff",
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: themeColors.secondary,
        position: "relative",
      }}
    >
      <Container maxWidth="lg">
        {showTitle && (
          <Box textAlign="center" mb={8}>
            <Typography
              variant="overline"
              sx={{
                color: themeColors.primary,
                fontWeight: 800,
                letterSpacing: 2,
                mb: 2,
                display: "block",
              }}
            >
              Who We Are
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                color: themeColors.textDark,
                fontFamily: "'Roboto Slab', serif",
                fontSize: { xs: "2rem", md: "3rem" },
                mb: 3,
              }}
            >
              A Premier Consulting Firm For{" "}
              <span style={{ color: themeColors.primary }}>
                Global Ambitions
              </span>
            </Typography>
            <Box
              sx={{
                width: 80,
                height: 4,
                bgcolor: themeColors.primary,
                margin: "0 auto",
                borderRadius: 2,
              }}
            />
          </Box>
        )}

        <Grid container spacing={4} sx={{ mb: 10 }}>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 5,
                bgcolor: "#fff",
                borderRadius: "24px",
                height: "100%",
                boxShadow: "0 20px 40px rgba(0,0,0,0.05)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                border: "1px solid #eee",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  color: "var(--primary-color)",
                  fontFamily: "'Roboto Slab', serif",
                }}
              >
                Global Minds, Local Expertise
              </Typography>
              <Typography
                sx={{
                  color: "#555",
                  fontSize: "1.1rem",
                  lineHeight: 1.8,
                  textAlign: "justify",
                }}
              >
                Globally minded, we offer solid expertise to empower our clients
                to take their chance on foreign markets to grow their business
                in India or elsewhere. A hybrid consultancy firm, we work with
                businesses of all sizes, from privately owned startups to
                multi-generational family businesses, helping them navigate the
                complexities of international trade. Our clients look for market
                entry services in India or expansion opportunities in Japan and
                Europe.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 5,
                bgcolor: "var(--primary-color)",
                borderRadius: "24px",
                height: "100%",
                color: "#fff",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
                boxShadow: "0 20px 40px rgba(26, 66, 161, 0.2)",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  bottom: -30,
                  right: -30,
                  opacity: 0.1,
                  transform: "rotate(-15deg)",
                }}
              >
                <LanguageIcon sx={{ fontSize: 200 }} />
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  fontFamily: "'Roboto Slab', serif",
                  color: "var(--treasury-color)",
                }}
              >
                Why Choose Us?
              </Typography>
              <Typography
                sx={{
                  fontSize: "1.1rem",
                  lineHeight: 1.8,
                  opacity: 0.9,
                  color: "var(--treasury-color)",
                }}
              >
                Our purpose is to accelerate your growth and ensure that your
                business remains compliant with local and international
                regulations in a rapidly changing business landscape. With an
                international team of over 170+ experts, we deliver tailor-made
                services designed to meet your specific business needs
                throughout the entire process of global establishment.
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Grid container spacing={4}>
          {cardsData.map((card, idx) => (
            <Grid item xs={12} md={4} key={idx}>
              <Card
                sx={{
                  height: "100%",
                  borderRadius: "20px",
                  transition: "all 0.3s ease",
                  border: "1px solid #eee",
                  overflow: "hidden",
                  "&:hover": {
                    transform: "translateY(-10px)",
                    boxShadow: "0 25px 50px rgba(0,0,0,0.1)",
                    "& .icon-box": {
                      transform: "scale(1.1) rotate(5deg)",
                    },
                  },
                }}
                elevation={0}
              >
                <CardContent sx={{ p: 4, textAlign: "center" }}>
                  <Box
                    className="icon-box"
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "20px",
                      background: card.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 30px",
                      transition: "all 0.4s ease",
                      boxShadow: `0 10px 20px ${card.color}44`,
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      fontFamily: "'Roboto Slab', serif",
                    }}
                  >
                    {card.title}
                  </Typography>
                  <Typography sx={{ color: "#666", lineHeight: 1.7 }}>
                    {card.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default AboutCompany;
